// Unit tab content — HP, movement, formation selector, hero abilities.
import type { SceneNode } from '@aod/protocol';
import { Badge } from '@aod/ui/components/ui/badge';
import { Progress } from '@aod/ui/components/ui/progress';
import { Separator } from '@aod/ui/components/ui/separator';
import { useChampion } from '../../champion/ChampionContext.js';
import { ChampionPanel } from '../../champion/ChampionPanel.js';
import { UnitStatusBadges } from '../../champion/ChampionStatusBadges.js';
import {
  type HeroQuest,
  type HeroQuestKind,
  type HuntTarget,
  heroIdOfUnit,
} from '../../heroes/quests.js';
import { type MessageKey, useT } from '../../i18n/index.js';
import { houseName } from '../../sea/houseNames.js';
import { merchantColour, merchantHouseOf } from '../../sea/merchantModel.js';
import { ArchetypeBadge, HeroAbilityBar, RoleBadge } from '../HeroPanel/index.js';
import type { AbilitySlot } from '../HeroPanel/types.js';
import { HeroQuestPanel } from './HeroQuestPanel.js';
import { Row } from './InfoPanelShell.js';
import { SpritePreview, unitPreviewUrl } from './SpritePreview.js';

const FORMATION_OPTIONS: ReadonlyArray<{ value: string; label: MessageKey }> = [
  { value: 'standard', label: 'formation.standard' },
  { value: 'line', label: 'formation.line' },
  { value: 'column', label: 'formation.column' },
  { value: 'wedge', label: 'formation.wedge' },
  { value: 'hold', label: 'formation.hold' },
  { value: 'phalanx', label: 'formation.phalanx' },
  { value: 'skirmish', label: 'formation.skirmish' },
];

export function UnitTabContent({
  unit,
  localPlayerIndex,
  onActivateAbility,
  onSelectAbility,
  onSetFormation,
  heroQuest = null,
  huntTargets = [],
  onDispatchHeroQuest,
}: {
  unit: SceneNode;
  localPlayerIndex: number;
  onActivateAbility: (unitId: string, slotIndex: number) => void;
  onSelectAbility: (unitId: string, slotIndex: number, abilityId: string) => void;
  onSetFormation: (unitId: string, formation: string) => void;
  /** The order this hero carries, while `heroes.quests` is on. */
  heroQuest?: HeroQuest | null;
  /** The enemy heroes a hunt may name. */
  huntTargets?: readonly HuntTarget[];
  /** Give the hero an order. */
  onDispatchHeroQuest?: (
    unitId: string,
    kind: HeroQuestKind,
    amount?: number,
    targetUnitId?: string,
  ) => boolean;
}) {
  const t = useT();
  const kind = (unit.props?.kind as string | undefined) ?? 'unit';
  const ownerId = (unit.props?.ownerId as number | undefined) ?? 0;
  const isOwn = ownerId === localPlayerIndex;
  // A foreign house's vessel: it belongs to no player, so the panel names the
  // house instead of numbering it.
  const house = merchantHouseOf(ownerId, unit.props?.seaHouse as number | undefined);
  const hp = (unit.props?.hp as number | undefined) ?? 0;
  const hpMax = (unit.props?.hpMax as number | undefined) ?? 0;
  const hpPct = hpMax > 0 ? Math.round((hp / hpMax) * 100) : 0;
  const remainingMv = unit.props?.remainingMovement as number | undefined;
  const maxMv = unit.props?.maxMovement as number | undefined;
  // Canteen (only present while the match has thirst on).
  const water = unit.props?.water as number | undefined;
  const waterMax = unit.props?.waterMax as number | undefined;
  const waterPct =
    water !== undefined && waterMax !== undefined && waterMax > 0
      ? Math.round((water / waterMax) * 100)
      : 0;
  const formation = (unit.props?.formation as string | undefined) ?? 'standard';
  // Ships (`sea.trade`): the hold and any plotted course.
  const cargoCapacity = unit.props?.cargoCapacity as number | undefined;
  const cargoAmount = (unit.props?.cargoAmount as number | undefined) ?? 0;
  const cargoKind = unit.props?.cargoKind as string | undefined;
  const sailing = unit.props?.sailing as number | undefined;
  const hero = unit.props?.hero as Record<string, unknown> | undefined;
  const isChampion = unit.props?.legend !== undefined;
  const championTick = useChampion()?.tick ?? null;
  // A Champion's panel carries its own HP and the primary action (Firestorm): lead with it
  // and drop the big portrait so it stays above the fold at 1366x768.
  const previewUrl = isChampion ? null : unitPreviewUrl(unit);
  const houseKey = house !== null ? houseName(house) : null;

  return (
    <div className="flex flex-col gap-3">
      {previewUrl && (
        <SpritePreview textureUrl={previewUrl} label={houseKey ? t(houseKey) : kind} />
      )}

      <div className="flex items-center gap-2">
        {house !== null ? (
          <Badge style={{ background: merchantColour(house), color: '#0d0f1c' }}>
            {t(houseKey ?? 'sea.house1')}
          </Badge>
        ) : (
          <Badge style={{ background: ownerId === 0 ? '#5ab1ff' : '#ff8a5a', color: '#0d0f1c' }}>
            {t('common.playerShort', { player: ownerId + 1 })}
          </Badge>
        )}
        <Badge variant="secondary">{house !== null ? t('sea.merchantVessel') : kind}</Badge>
      </div>

      {isChampion && <ChampionPanel unit={unit} />}
      {!isChampion && <UnitStatusBadges unit={unit} tick={championTick} />}

      {hpMax > 0 && !isChampion && (
        <div className="flex flex-col gap-1">
          <Row label={t('common.hpLower')}>
            {hp} / {hpMax}
          </Row>
          <Progress value={hpPct} aria-label={t('unit.health')} />
        </div>
      )}

      {water !== undefined && waterMax !== undefined && waterMax > 0 && (
        <div className="flex flex-col gap-1" data-testid="unit-water">
          <Row label={t('unit.water')}>
            {water} / {waterMax}
          </Row>
          <Progress value={waterPct} aria-label={t('unit.waterLevel')} />
        </div>
      )}

      {cargoCapacity !== undefined && (
        <div className="flex flex-col gap-1">
          <Row label={t('unit.cargo')}>
            {cargoAmount} / {cargoCapacity}
            {cargoAmount > 0 && cargoKind ? ` ${cargoKind}` : ''}
          </Row>
          {sailing !== undefined && sailing > 0 && <Row label={t('unit.sailing')}>{sailing}</Row>}
          <p className="text-[11px] text-muted-foreground">{t('unit.shipHint')}</p>
        </div>
      )}

      {(remainingMv !== undefined || maxMv !== undefined) && (
        <Row label={t('unit.movement')}>
          {remainingMv ?? '?'} / {maxMv ?? '?'}
        </Row>
      )}

      {/* Formation dropdown for owned units */}
      {isOwn && (
        <div className="flex flex-col gap-1.5">
          <span className="text-[11px] font-medium text-muted-foreground">
            {t('unit.formation')}
          </span>
          <select
            className="h-8 rounded-md border border-input bg-background px-2 text-[11px] text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={formation}
            onChange={(e) => onSetFormation(unit.id, e.target.value)}
          >
            {FORMATION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.label)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Hero-specific content */}
      {hero && (
        <>
          <Separator />
          <HeroContent
            unit={unit}
            hero={hero}
            isOwn={isOwn}
            onActivateAbility={onActivateAbility}
            onSelectAbility={onSelectAbility}
            heroQuest={heroQuest}
            huntTargets={huntTargets}
            onDispatchHeroQuest={onDispatchHeroQuest}
          />
        </>
      )}

      {!hero && unit.props?.legend === undefined && (
        <>
          <Separator />
          <p className="text-[11px] text-muted-foreground">{t('unit.regular')}</p>
        </>
      )}
    </div>
  );
}

function HeroContent({
  unit,
  hero: h,
  isOwn,
  onActivateAbility,
  onSelectAbility,
  heroQuest,
  huntTargets,
  onDispatchHeroQuest,
}: {
  unit: SceneNode;
  hero: Record<string, unknown>;
  isOwn: boolean;
  onActivateAbility: (unitId: string, slotIndex: number) => void;
  onSelectAbility: (unitId: string, slotIndex: number, abilityId: string) => void;
  heroQuest: HeroQuest | null;
  huntTargets: readonly HuntTarget[];
  onDispatchHeroQuest?: (
    unitId: string,
    kind: HeroQuestKind,
    amount?: number,
    targetUnitId?: string,
  ) => boolean;
}) {
  const t = useT();
  const archetype = (h.archetype as string) ?? 'Warlord';
  const role = (h.role as string) ?? 'General';
  const level = (h.level as number) ?? 1;
  const xp = (h.xp as number) ?? 0;
  const xpToNext = (h.xpToNext as number) ?? 100;
  const loyalty = (h.loyalty as number) ?? 60;
  const attrs = h.attributes as Record<string, number> | undefined;
  const traits = (h.traits as string[]) ?? [];
  const abilitySlots = (h.abilitySlots as AbilitySlot[]) ?? [];
  const isWounded = (h.isWounded as boolean) ?? false;
  const isCaptured = (h.isCaptured as boolean) ?? false;
  const unusedPicks = (h.unusedAbilityPicks as number) ?? 0;
  const aura = h.aura as Record<string, unknown> | undefined;
  const xpPct = xpToNext > 0 ? Math.min(100, Math.round((xp / xpToNext) * 100)) : 100;

  return (
    <>
      <div className="flex items-center gap-1.5">
        <ArchetypeBadge archetype={archetype} />
        <RoleBadge role={role} />
      </div>

      <div className="space-y-1">
        <Row label={t('hero.level')}>
          <span className="font-semibold">{level}</span>
          <span className="text-muted-foreground/70">/20</span>
        </Row>
        <div className="space-y-0.5">
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{t('hero.xp')}</span>
            <span>
              {xp}/{xpToNext}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden">
            <div
              className="h-full rounded-full bg-amber-500 transition-all"
              style={{ width: `${xpPct}%` }}
            />
          </div>
        </div>
      </div>

      {(isWounded || isCaptured) && (
        <div className="flex gap-1.5">
          {isWounded && (
            <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:text-red-400">
              {t('hero.wounded')}
            </span>
          )}
          {isCaptured && (
            <span className="rounded bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-medium text-orange-700 dark:text-orange-400">
              {t('hero.captured')}
            </span>
          )}
        </div>
      )}

      <div>
        <span className="text-[10px] font-semibold tracking-wide text-muted-foreground">
          {t('hero.attributes')}
        </span>
        <div className="mt-1 grid grid-cols-5 gap-1">
          {(['martial', 'intellect', 'charisma', 'cunning', 'faith'] as const).map((attr) => (
            <div key={attr} className="flex flex-col items-center rounded bg-muted/30 px-1 py-1">
              <span className="text-[9px] text-muted-foreground">{t(`hero.attr.${attr}`)}</span>
              <span className="text-xs font-mono font-semibold tabular-nums">
                {attrs?.[attr] ?? 0}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-0.5">
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>{t('hero.loyalty')}</span>
          <span
            className={
              loyalty < 30
                ? 'text-red-700 dark:text-red-400'
                : loyalty < 50
                  ? 'text-amber-700 dark:text-amber-400'
                  : ''
            }
          >
            {loyalty}/100
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              loyalty < 30 ? 'bg-red-500' : loyalty < 50 ? 'bg-amber-500' : 'bg-emerald-500'
            }`}
            style={{ width: `${loyalty}%` }}
          />
        </div>
      </div>

      {traits.length > 0 && (
        <div>
          <span className="text-[10px] font-semibold tracking-wide text-muted-foreground">
            {t('hero.traits')}
          </span>
          <div className="mt-1 flex flex-wrap gap-1">
            {traits.map((trait) => (
              <span
                key={trait}
                className="rounded bg-purple-500/10 px-1.5 py-0.5 text-[10px] text-purple-700 dark:text-purple-300"
              >
                {trait}
              </span>
            ))}
          </div>
        </div>
      )}

      {aura && (
        <div>
          <span className="text-[10px] font-semibold tracking-wide text-muted-foreground">
            {t('hero.aura')}
          </span>
          <div className="mt-1 rounded bg-blue-500/10 px-2 py-1 text-[10px] text-blue-700 dark:text-blue-300">
            {t('hero.auraStats', {
              radius: String(aura.radius),
              attack: String(aura.attackMultiplier),
              defense: String(aura.defenseMultiplier),
            })}
          </div>
        </div>
      )}

      <HeroAbilityBar
        unitId={unit.id}
        abilitySlots={abilitySlots}
        role={role}
        level={level}
        isWounded={isWounded}
        unusedPicks={unusedPicks}
        onActivateAbility={onActivateAbility}
        onSelectAbility={onSelectAbility}
      />

      {/* The hero's order (`heroes.quests`) — only your own hero takes one. */}
      {isOwn && (
        <HeroQuestPanel
          heroId={heroIdOfUnit(unit.id) ?? 0}
          heroName={(h.name as string | undefined) ?? ''}
          quest={heroQuest}
          huntTargets={huntTargets}
          onDispatchHeroQuest={onDispatchHeroQuest}
        />
      )}
    </>
  );
}
